import React, { useState } from "react";
import { Search, User, Home } from "lucide-react";
import { Button } from "../shared/Button";
import { panel } from "../../api/modulos.service";
import { etiquetaEstado } from "../../utils/estados";
import { estilos as est, Cabecera, Vacio, Entrada, Aviso } from "./base";
import { mostrarDia } from "../../utils/fecha";

/**
 * LÍNEA DE TIEMPO DE UNA PERSONA Y DE UNA CASA.
 *
 * **Datos verificables, ninguna etiqueta.** El sistema no dice «persona
 * peligrosa»: dice «18 visitas, 0 rechazos, 5 casas» y deja que quien mira
 * decida. Poner una etiqueta ahí sería convertir una estadística en una
 * acusación, y el sistema no tiene con qué sostenerla.
 */

/* `fecha` es para una MARCA DE TIEMPO —`entryTime`, `occurredAt`,
 * `receivedAt`—, donde la hora de El Salvador es la correcta.
 *
 * `visit.date` NO es una marca de tiempo: es un día de calendario que se
 * guardó desde un `<input type="date">`, y Mongoose interpreta en UTC una
 * fecha sin hora, así que en la base queda la medianoche UTC. Formatearlo en
 * UTC-6 resta seis horas y enseña el día anterior. Para eso está
 * `mostrarDia`, en `utils/fecha.js`.
 *
 * Los dos conviven en este archivo a propósito: aplicar el mismo ayudante a
 * los dos arregla uno y rompe el otro. */
const fecha = (f) =>
  new Date(f).toLocaleString("es-SV", { timeZone: "America/El_Salvador", dateStyle: "short", timeStyle: "short" });

export default function Historial() {
  const [modo, setModo] = useState("persona");
  const [nombre, setNombre] = useState("");
  const [poligono, setPoligono] = useState("");
  const [casa, setCasa] = useState("");
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");

  /* La consulta va aparte del `submit` para que «Reintentar» pueda repetirla
     con lo que la persona ya escribió, sin pedirle que vuelva a pulsar
     «Buscar». */
  const consultar = async () => {
    setError(""); setCargando(true); setDatos(null);
    try {
      setDatos(
        modo === "persona"
          ? await panel.historialPersona(nombre)
          : await panel.historialCasa(poligono, casa)
      );
    } catch (err) {
      setError(err?.response?.data?.message || "No se pudo consultar");
    } finally { setCargando(false); }
  };

  const buscar = (e) => {
    e.preventDefault();
    consultar();
  };

  return (
    <div style={est.seccion}>
      <Cabecera titulo="Historial" subtitulo="Busque una persona o una casa." icono={Search} />

      <div style={{ display: "flex", gap: "var(--space-2)" }}>
        {[["persona", "Una persona", User], ["casa", "Una casa", Home]].map(([v, t, I]) => (
          <Button key={v} variant={modo === v ? "primary" : "outline"} leftIcon={I} onClick={() => { setModo(v); setDatos(null); }}>
            {t}
          </Button>
        ))}
      </div>

      <form onSubmit={buscar} style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap", alignItems: "flex-end" }}>
        {modo === "persona" ? (
          <div style={{ flex: "1 1 260px" }}>
            <Entrada etiqueta="Nombre exacto" value={nombre} onChange={(e) => setNombre(e.target.value)} required />
          </div>
        ) : (
          <>
            <div style={{ flex: "0 1 120px" }}>
              <Entrada etiqueta="Polígono" value={poligono} onChange={(e) => setPoligono(e.target.value)} required />
            </div>
            <div style={{ flex: "0 1 120px" }}>
              <Entrada etiqueta="Casa" value={casa} onChange={(e) => setCasa(e.target.value)} required />
            </div>
          </>
        )}
        <Button type="submit" variant="primary" loading={cargando}>Buscar</Button>
      </form>

      {/* El fallo va en `Aviso`, no en `Vacio`. Vestir «no se pudo consultar»
          con la misma caja gris que «sin visitas registradas» hacía que un
          servidor caído se leyera como una persona sin historial — y de esas
          dos respuestas sólo una pide llamar a alguien. */}
      {error && (
        <Aviso>
          {error}
          <div style={{ marginTop: "var(--space-2)" }}>
            <Button size="sm" variant="outline" loading={cargando} onClick={consultar}>
              Reintentar
            </Button>
          </div>
        </Aviso>
      )}

      {datos && modo === "persona" && (
        <>
          <div style={{ display: "flex", gap: "var(--space-4)", flexWrap: "wrap" }}>
            {[
              ["Visitas", datos.resumen?.visitas],
              ["Casas visitadas", datos.resumen?.casasVisitadas],
              ["Accesos rechazados", datos.resumen?.accesosRechazados],
              ["Incidentes", datos.resumen?.incidentesRelacionados],
            ].map(([t, v]) => (
              <div key={t} style={{ ...est.tarjeta, minWidth: "140px" }}>
                <p style={{ margin: 0, fontSize: "var(--text-2xl)", fontWeight: 700, color: "var(--text)" }}>{v ?? 0}</p>
                <p style={{ ...est.subtitulo, margin: 0 }}>{t}</p>
              </div>
            ))}
          </div>

          <h3 style={{ ...est.titulo, fontSize: "var(--text-lg)" }}>Movimientos</h3>
          {(datos.visitas || []).length === 0 ? <Vacio>Sin visitas registradas.</Vacio> : (
            <div>
              {datos.visitas.map((v) => (
                <div key={v._id} style={{ display: "flex", gap: "var(--space-3)", padding: "var(--space-3) 0", borderBottom: "1px solid var(--border-light)", fontSize: "var(--text-sm)" }}>
                  <span style={{ color: "var(--text-tertiary)", flexShrink: 0 }}>{mostrarDia(v.date, { dateStyle: "medium" })}</span>
                  <span style={{ color: "var(--text)" }}>
                    Casa {v.polygon}-{v.houseNumber} · {etiquetaEstado(v.status)}
                    {v.plate ? ` · ${v.plate}` : ""}
                    {v.entryAccessPoint?.name ? ` · ${v.entryAccessPoint.name}` : ""}
                    {v.enteredBy?.name ? ` · lo registró ${v.enteredBy.name}` : ""}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {datos && modo === "casa" && (
        <>
          {[
            ["Residentes", datos.residentes, (r) => `${r.name}${r.email ? ` · ${r.email}` : ""}`],
            ["Vehículos", datos.vehiculos, (v) => `${v.plate} · ${[v.brand, v.model, v.color].filter(Boolean).join(" ")}`],
            ["Visitantes frecuentes", datos.visitantesFrecuentes, (r) => `${r.name} · ${r.startTime}–${r.endTime}`],
            /* Sólo las visitas pasan por `etiquetaEstado`: los estados de un
               paquete, una solicitud o una reserva son otros catálogos y
               traducirlos con la tabla de visitas los dejaría peor. */
            ["Visitas recientes", datos.visitas, (v) => `${mostrarDia(v.date, { dateStyle: "medium" })} · ${v.visitorName} · ${etiquetaEstado(v.status)}`],
            ["Incidentes", datos.incidentes, (i) => `${fecha(i.occurredAt)} · ${i.type}`],
            ["Paquetes", datos.paquetes, (p) => `${fecha(p.receivedAt)} · ${p.carrier || "—"} · ${p.status}`],
            ["Solicitudes", datos.solicitudes, (s) => `${s.title} · ${s.status}`],
            ["Reservas", datos.reservas, (r) => `${r.amenity?.name} · ${fecha(r.startAt)} · ${r.status}`],
          ].map(([titulo, filas, pintar]) => (
            <div key={titulo} style={{ display: "grid", gap: "var(--space-2)" }}>
              <h3 style={{ ...est.titulo, fontSize: "var(--text-lg)" }}>{titulo} ({(filas || []).length})</h3>
              {(filas || []).length === 0 ? (
                <p style={est.subtitulo}>Nada registrado.</p>
              ) : (
                filas.map((f) => (
                  <div key={f._id} style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", padding: "var(--space-2) 0", borderBottom: "1px solid var(--border-light)" }}>
                    {pintar(f)}
                  </div>
                ))
              )}
            </div>
          ))}
        </>
      )}
    </div>
  );
}
